/**
 * The email client's strings (piece 32), kept beside its rules and spread into
 * the one lookup in `strings.ts`, so `t()` stays the only way a screen reads
 * text. 🔨 This file imports nothing, because the lookup imports it.
 */
export const MAIL_STRINGS = {
  'whatsNew.area.email': 'Email',
  'jobs.purgeOpenTrackingDetail.label': 'Purge open-tracking detail',
  'jobs.purgeOpenTrackingDetail.purpose': 'Clears the browser and network address of every mail open older than ninety days and keeps the open itself.',

  // The client's frame and folder tree.
  'mail.title': 'Email',
  'mail.subtitle': 'Mail client',
  'mail.checkForMail': 'Check for mail',
  'mail.checking': 'Checking for mail…',
  'mail.checked': 'Mail checked: new messages',
  'mail.folders': 'Folders',
  'mail.mailbox.departed': 'Left the company · read only',
  'mail.mailbox.quota': 'Mailbox full',
  'mail.mailbox.shared': 'Shared',
  'mail.mailbox.none': 'No mailbox is set up for you yet. An administrator adds one in Settings › Email › Mail Settings, under Mailboxes.',
  'mail.folder.unread': 'unread',
  'mail.allFolders': 'All folders',
  // 🔒 The folder rail's foot (decision 1474): the word beside the glyph while open, the full sentence as the control's name.
  'mail.rail.collapse': 'Collapse',
  'mail.rail.collapseLabel': 'Collapse the folders',
  'mail.rail.expand': 'Expand the folders',

  // The list and its search.
  'mail.search.label': 'Search mail',
  'mail.search.placeholder': 'Sender, recipient, subject or words in the message',
  'mail.search.run': 'Search',
  'mail.search.needsWord': 'Type a word to search for, or open More filters.',
  'mail.search.more': 'More Filters',
  'mail.search.fewer': 'Fewer Filters',
  'mail.search.clear': 'Clear Search',
  'mail.search.from': 'From',
  'mail.search.to': 'To',
  'mail.search.subject': 'Subject',
  'mail.search.body': 'Words in the message',
  'mail.search.since': 'Received on or after',
  'mail.search.until': 'Received on or before',
  'mail.search.hasAttachment': 'Has an attachment',
  'mail.search.everyFolder': 'Search every folder of this mailbox',
  'mail.search.results': 'matching messages',
  'mail.search.newest': 'Showing the newest',
  'mail.search.narrow': 'search to narrow the rest',
  'mail.list.recordType': 'Messages',
  'mail.list.empty': 'No messages here',
  'mail.list.pickFolder': 'Pick a folder to read its mail.',
  'mail.view.all': 'All messages',
  'mail.view.unread': 'Unread',
  'mail.view.flagged': 'Flagged',
  'mail.mark.attachment': 'Attachment',
  'mail.mark.flagged': 'Flagged',
  'mail.mark.high': 'High priority',
  'mail.mark.warning': 'Warning',
  'mail.noSubject': '(No Subject)',
  // 🔒 The mail list (decision 1478): the order, the day headers, the row's marks and its actions on hover or long press.
  'mail.list.sort': 'Sort',
  'mail.list.sort.newest': 'Newest first',
  'mail.list.sort.oldest': 'Oldest first',
  'mail.list.sort.sender': 'Sender',
  'mail.list.sort.subject': 'Subject',
  'mail.list.today': 'Today',
  'mail.list.yesterday': 'Yesterday',
  'mail.list.messages': 'messages',
  'mail.list.rowActions': 'Actions on this message',
  'mail.list.moreActions': 'More actions',
  'mail.action.archive': 'Archive',

  // The bulk actions.
  'mail.action.markRead': 'Mark Read',
  'mail.action.markUnread': 'Mark Unread',
  'mail.action.flag': 'Flag',
  'mail.action.unflag': 'Unflag',
  'mail.action.move': 'Move To',
  'mail.action.moveHint': 'Choose a folder to move the selected mail to',
  'mail.action.delete': 'Delete',
  'mail.action.restore': 'Restore',
  'mail.action.report': 'Report Spam or Phishing',
  'mail.action.reportTitle': 'Report Spam or Phishing',
  'mail.action.reportBody': 'The mail moves to Quarantine for review, and the report is written in the audit log. Nothing is deleted.',
  'mail.quarantine.confirmTitle': 'Confirm Rejection',
  'mail.quarantine.confirmBody': 'The mail leaves the review for Trash, where it can still be restored.',
  'mail.action.reportConfirm': 'Report',
  'mail.action.needsSelection': 'Select one or more messages first.',
  'mail.action.readOnly': 'This mailbox belongs to somebody who has left; it is read only.',
  'mail.action.done': 'messages changed',
  'mail.action.refused': 'refused',

  // The reading pane.
  'mail.pane.empty': 'Pick a message to read it here.',
  'mail.pane.from': 'From',
  'mail.pane.to': 'To',
  'mail.pane.cc': 'Cc',
  'mail.pane.replyTo': 'Reply-To',
  'mail.pane.replyToDiffers': 'Replies to this message go to a different address from the sender’s:',
  'mail.pane.date': 'Date',
  'mail.pane.security': 'Security',
  'mail.pane.spf': 'SPF',
  'mail.pane.dkim': 'DKIM',
  'mail.pane.dmarc': 'DMARC',
  // Decision 1201: the signer's name beside a Valid signature pill.
  'mail.pane.signedBy': 'Signed by',
  'mail.pane.warning': 'Be careful with this message.',
  'mail.pane.quarantined': 'This message is in Quarantine.',
  'mail.pane.quarantinedByScreening': 'The security screening held it.',
  'mail.pane.quarantinedByPerson': 'Somebody reported it as spam or phishing.',
  'mail.pane.remoteImages': 'pictures from the internet are hidden, to keep senders from learning you opened this.',
  'mail.pane.showImages': 'Show Pictures',
  'mail.pane.inlineImages': 'pictures inside the message',
  'mail.pane.scanPending': 'waiting for its scan',
  'mail.pane.scanBlocked': 'blocked',
  'mail.pane.attachmentCount.one': 'Attachment',
  'mail.pane.attachmentCount.many': 'Attachments',
  'mail.pane.downloadAll': 'Download all',
  'mail.pane.downloadAllWaits': 'Download all waits until every attachment has cleared its scan',
  // T357: the bound on Download all, read by the pane's greyed control and the zip route's refusal alike.
  'mail.pane.downloadAllTooLarge.lead': 'These attachments come to',
  'mail.pane.downloadAllTooLarge.over': 'over the',
  'mail.pane.downloadAllTooLarge.tail': 'Download all carries; open each one from its own card',
  'mail.pane.thread': 'Conversation',
  'mail.pane.threadCurrent': 'this message',
  'mail.pane.viewHeaders': 'View Original Headers',
  'mail.pane.headersTitle': 'Original headers',
  'mail.pane.headersHint': 'The header block exactly as the message arrived. The security results above were read from it when the message first arrived.',
  'mail.pane.print': 'Print',
  'mail.pane.close': 'Close',
  'mail.pane.tagged': 'Tagged to',
  'mail.pane.taggedInherited': 'taken from the first message of the conversation',
  'mail.pane.untag': 'Remove Tag',
  'mail.pane.tagToRecord': 'Tag to Record',
  'mail.pane.sendToAccounting': 'Send to Accounting',
  'mail.pane.sentToAccounting': 'Sent to accounting at',
  'mail.pane.sentToAccountingWaiting': 'Waiting in Outbox to go to accounting at',
  'mail.pane.sentToAccountingDone': 'Sent to accounting. It reaches the accounting mailbox with its next check for mail.',
  'mail.pane.sentToAccountingHeld': 'Waiting in Outbox to go to accounting, under your undo delay.',
  'mail.pane.bounced': 'This message could not be delivered.',
  'mail.pane.bounceOf': 'This is the delivery failure for',
  'mail.pane.open': 'Open',
  'mail.pane.delivery': 'Delivery',

  // 🔒 The sender menu (decision 1477): the sender's name in the header, and a right-click on the header, open one menu.
  'mail.sender.menu': 'Actions on the sender',
  'mail.sender.copy': 'Copy address',
  'mail.sender.copied': 'The address is copied.',
  'mail.sender.copyFailed': 'This browser did not let the page copy the address; select it in the header and copy it instead.',
  'mail.sender.writeTo': 'Write to',
  'mail.sender.newEvent': 'New event',
  'mail.sender.messages': 'Messages from this sender',
  'mail.sender.attachments': 'Attachments from this sender',
  'mail.sender.block': 'Block sender',
  'mail.sender.blockTitle': 'Block This Sender',
  'mail.sender.blockLead': 'Mail from',
  'mail.sender.blockBody': 'is held in Quarantine for everyone from now on. An administrator retires the block under Spam and Quarantine in the Mail Settings window.',
  'mail.sender.blockConfirm': 'Block',
  'mail.sender.blockReason': 'Blocked from the mail client',
  'mail.sender.blocked': 'The sender is blocked, and their mail is held in Quarantine from now on.',
  'mail.sender.openPerson': 'Open the person',
  'mail.sender.createPerson': 'Create a person',
  'mail.sender.findEverywhere': 'Find across the application',
  // Contact extraction to Person (decision 1506): what the pre-filled Add Person window says when the message names no company.
  'mail.sender.noCompany': 'The message names no company we know. Choose the company this person works at, or add it with Add New Company.',
  'mail.sender.companyNamed': 'The signature names a company that matches none here:',
  'mail.sender.companyNamedAsk': 'Choose the company this person works at, or add it with Add New Company.',
  // Follows the new person's name: the window has closed, so the notice names who was made.
  'mail.sender.tagFailed': 'was added to People, and the message was not tagged to them:',

  // Tag to Record and Body as Note.
  'mail.tag.title': 'Tag to Record',
  'mail.tag.hint': 'The message stays in the mailbox; the record shows it. Tag the first message of a conversation and the replies follow.',
  'mail.tag.kind': 'Record',
  'mail.tag.find': 'Find',
  'mail.tag.findPlaceholder': 'Type part of a name or number',
  'mail.tag.none': 'Nothing found.',
  'mail.tag.chosen': 'Chosen',
  'mail.tag.suggested': 'Suggested by your rule',
  'mail.tag.suggestedHint': 'Matched on who sent the message. Tag confirms it, or find another record below.',
  'mail.tag.bodyAsNote': 'Also add the message as a note on the record',
  'mail.tag.save': 'Tag',
  'mail.tag.saved': 'Message tagged',
  'mail.tag.kind.workOrder': 'Work order',
  'mail.tag.kind.deal': 'Deal',
  'mail.tag.kind.companyRecord': 'Company',
  'mail.tag.kind.employmentRecord': 'Employment',
  'mail.tag.kind.personRecord': 'Person',
  'mail.tag.kind.invoice': 'Invoice',
  'mail.print.title': 'Print',
  'mail.print.hint': 'Most mail is better kept on the record it is about than on paper. Tag it with the body as a note, or print it.',
  'mail.print.asNote': 'Tag to Record with the Body as a Note',
  'mail.print.paper': 'Print on Paper',
  // Decision 1539: the printed sheet's own labels; the rest it shares with the reading pane.
  'mail.sheet.mailbox': 'Mailbox',
  'mail.sheet.page': 'Page',
  'mail.sheet.of': 'of',

  // The quarantine review.
  'mail.quarantine.title': 'Quarantined Mail',
  'mail.quarantine.subtitle': 'Rejected email review',
  'mail.quarantine.intro': 'Mail the security screening held and mail people reported. Nothing here is released on its own: release what is safe, and confirm the rest.',
  'mail.quarantine.release': 'Release to Inbox',
  'mail.quarantine.confirm': 'Confirm Rejection',
  'mail.quarantine.empty': 'Nothing is waiting for review.',
  // Decision 0961: a reader without the review permission sees how many are held, never the folder.
  'mail.quarantine.held': 'Held in Quarantine',
  'mail.quarantine.heldHint': 'Somebody allowed to review quarantine releases or rejects these.',
  'mail.quarantine.col.from': 'From',
  'mail.quarantine.col.subject': 'Subject',
  'mail.quarantine.col.reason': 'Reason and Failed Check',
  'mail.quarantine.col.date': 'Received',
  'mail.quarantine.reason.securityScreening': 'Held by screening',
  'mail.quarantine.reason.userReported': 'Reported by a person',
  'mail.quarantine.reportedCheck': 'A person reported it as spam or phishing',
  'mail.quarantine.hostCheck': 'The mail host filed it as junk before it arrived',
  'mail.quarantine.review': 'Review',
  'mail.quarantine.denied': 'Reviewing quarantined mail is not part of your role.',

  // Settings › Email › Mailboxes.
  'mail.settings.title': 'Mailboxes',
  'mail.settings.intro': 'Every mailbox the mail program reads: a person’s own, a department’s or a module’s. Open a shared mailbox to choose who reads it; the mailbox itself is created at your mail host by an administrator.',
  'mail.settings.recordType': 'Mailboxes',
  'mail.settings.view.all': 'All mailboxes',
  'mail.settings.view.shared': 'Shared',
  'mail.settings.view.delegated': 'Delegated',
  'mail.settings.view.lists': 'Distribution lists',
  'mail.settings.view.departed': 'Departed',
  'mail.settings.col.name': 'Name',
  'mail.settings.col.address': 'Address',
  'mail.settings.col.kind': 'Kind and Owner',
  'mail.settings.col.readers': 'Readers',
  'mail.settings.col.quota': 'Quota',
  'mail.settings.empty': 'No mailbox is set up yet.',
  'mail.settings.kind.personal': 'Personal',
  'mail.settings.kind.department': 'Department',
  'mail.settings.kind.module': 'Module',
  'mail.settings.list': 'Distribution list',
  'mail.settings.departed': 'Departed',
  'mail.settings.noQuota': 'None',
  'mail.settings.used': 'used',
  'mail.unit.kb': 'KB',
  'mail.unit.mb': 'MB',

  // Compose, Reply, Reply All and Forward (part b).
  'mail.compose.new': 'New Message',
  'mail.compose.opening': 'Opening a New Message…',
  'mail.compose.title': 'New message',
  'mail.compose.from': 'From',
  'mail.compose.to': 'To',
  'mail.compose.cc': 'Cc',
  'mail.compose.bcc': 'Bcc',
  'mail.compose.addressEntity': 'address',
  'mail.compose.useAddress': 'Use this address',
  'mail.compose.subject': 'Subject',
  'mail.compose.body': 'Message',
  'mail.compose.attachments': 'Attachments',
  'mail.compose.fromRecord': 'Attach a File from the Tagged Record',
  'mail.compose.hideRecordFiles': 'Hide the Record’s Files',
  'mail.compose.recordFilesNone': 'The tagged record has no file you can attach.',
  'mail.compose.dropFailed': 'That file could not be attached.',
  'mail.compose.remove': 'Remove',
  'mail.compose.readReceipt': 'Request a read receipt',
  'mail.compose.highPriority': 'High priority',
  'mail.compose.addCc': 'Add a Cc line',
  'mail.compose.addBcc': 'Add a Bcc line',
  'mail.compose.attach': 'Attach',
  'mail.compose.more': 'More for this message',
  'mail.compose.actions': 'Message actions',
  'mail.compose.headers': 'Message headers',
  'mail.compose.sendKeys': 'Ctrl+Enter',
  'mail.compose.pickTime': 'Choose when it goes first',
  'mail.compose.send': 'Send',
  'mail.compose.sending': 'Sending…',
  'mail.compose.close': 'Close and Keep the Draft',
  'mail.compose.keptAsDraft': 'Kept in Drafts.',
  'mail.compose.discard': 'Discard',
  'mail.compose.discardTitle': 'Discard this draft?',
  'mail.compose.discardBody': 'The draft and its attachments come off the message. Files already in the application stay where they are.',
  'mail.compose.saving': 'Saving…',
  'mail.compose.saved': 'Draft saved',
  'mail.compose.sent': 'Sent.',
  'mail.compose.failed': 'Not sent: the mail service refused it. It waits in Outbox, where you can try again.',
  'mail.compose.noRecipient': 'Name at least one recipient.',
  'mail.compose.via.sendAs': 'sending as',
  'mail.compose.via.onBehalf': 'On behalf of',
  'mail.pane.reply': 'Reply',
  'mail.pane.replyAll': 'Reply all',
  'mail.pane.forward': 'Forward',
  'mail.pane.sentBy': 'Sent by',
  'mail.compose.aboutRecord': 'Send Email',

  // Settings › Email › Sending (part b; decision 0791).
  'mail.sending.title': 'Sending',
  'mail.sending.intro': 'Which mailbox each module’s mail goes from. The person who presses send is recorded on every message either way.',
  'mail.sending.modulesTitle': 'Module senders',
  'mail.sending.modulesHint': 'A module left on “The sender’s own mailbox” sends a document from whoever presses Send. Naming a department or module mailbox sends every one of that module’s messages from it, and its copy lands in that mailbox’s Sent folder.',
  'mail.sending.ownMailbox': 'The sender’s own mailbox',
  'mail.sending.saved': 'Module senders saved.',
  'mail.sending.testTitle': 'Send Test Message',
  'mail.sending.testHint': 'Sends one message to your own address through the mail transport and stores what the host answered. Step 4 of Set up company mail reads the newest one.',
  'mail.sending.testNone': 'No test message has been sent yet.',
  'mail.sending.testAccepted': 'Accepted by the host',
  'mail.sending.testRejected': 'Refused by the host',
  'mail.sending.testDeferred': 'Deferred by the host',
  'mail.sending.testFailed': 'The transport failed',
  'mail.sending.testTo': 'to',
  'mail.module.company': 'Company',
  'mail.module.person': 'Person',
  'mail.module.location': 'Location',
  'mail.module.site': 'Site',
  'mail.module.deal': 'Deal',
  'mail.module.quote': 'Quote',
  'mail.module.salesOrder': 'Sales order',
  'mail.module.invoice': 'Invoice',
  'mail.module.purchaseOrder': 'Purchase order',
  'mail.module.workOrder': 'Work order',
  'mail.module.item': 'Item',
  'mail.module.accounting': 'Accounting',
  'mail.module.expenses': 'Expenses',

  // The mail sent about a record, on its page, and its opens (decision 0162).
  'mail.receipt.label': 'Read receipt',
  'mail.receipt.asked': 'The sender asked for a read receipt at',
  'mail.receipt.send': 'Send Receipt',
  'mail.receipt.sending': 'Sending the receipt.',
  'mail.receipt.sentTo': 'Read receipt sent to',
  'mail.receipt.held': 'Read receipt waiting in the Outbox for',
  'mail.receipt.alreadySent': 'You sent a read receipt for this message.',
  'mail.recordMessages.title': 'Email',
  'mail.recordMessages.received': 'received',
  'mail.recordMessages.capNote': 'Only the newest {count} messages are listed, sent and received together. Older mail stays in its mailbox.',
  'mail.recordMessages.to': 'to',
  'mail.recordMessages.from': 'from',
  'mail.recordMessages.notSent': 'not sent',
  'mail.recordMessages.bounced': 'bounced',
  'mail.opened.label': 'Link opened',
  'mail.opened.time': 'time',
  'mail.opened.times': 'times',
  'mail.opened.last': 'last',

  // Signatures (part b).
  'mail.signature.title': 'Your Signatures',
  'mail.signature.hint': 'Insert your name, title, phone and email as placeholders, so a change to your record changes the signature by itself. Each message you write starts with it, and you can change it on that message alone.',
  'mail.signature.position': 'Where it sits against the company block',
  'mail.signature.position.prefix': 'Before the company block',
  'mail.signature.position.append': 'After the company block',
  'mail.signature.position.standalone': 'On its own, without the company block',
  'mail.signature.company': 'Company block',
  'mail.signature.noCompany': 'No company block is set.',
  'mail.signature.companyHint': 'The company block is set in Settings › Email › Mail Settings, under Mailboxes.',
  'mail.signature.token.name': 'Name',
  'mail.signature.token.title': 'Title',
  'mail.signature.token.phone': 'Phone',
  'mail.signature.token.email': 'Email',
  'mail.sending.companyTitle': 'Company signature block',
  'mail.sending.companyHint': 'The block every message a person writes carries, before or after their own. Automated mail, such as an overdue reminder, carries this block alone. The legal footer is separate, under Email templates and footer.',
  'mail.sending.companySaved': 'Company block saved.',

  // Held, scheduled and undone sends, and the Outbox (part b).
  'mail.compose.sendLater': 'Send Later',
  'mail.compose.sendAt': 'Send at',
  'mail.compose.schedule': 'Schedule',
  'mail.compose.cancelLater': 'Send Now Instead',
  'mail.compose.heldUntil': 'Sending at',
  'mail.compose.undo': 'Undo',
  'mail.compose.undone': 'Not sent. The message is back in Drafts.',
  'mail.outbox.held': 'Waiting in Outbox, to be sent at',
  'mail.outbox.cancel': 'Cancel Sending',
  'mail.outbox.failed': 'Not sent: the mail service refused it.',
  'mail.outbox.retry': 'Try Again',
  'mail.undo.title': 'Undo send',
  'mail.undo.hint': 'How long a message waits in Outbox after you press Send, so you can take it back.',
  'mail.undo.organisation': 'The organization’s',
  'mail.undo.off': 'Send at once',
  'mail.undo.seconds': 'seconds',
  'mail.sending.undoTitle': 'Undo send',
  'mail.sending.undoHint': 'How long every message waits in Outbox after Send, for a person who has not chosen their own. Zero sends at once.',
  'mail.sending.undoSaved': 'Undo delay saved.',

  // Filter rules (part b).
  'mail.filters.title': 'Filter Rules',
  'mail.filters.hint': 'Rules run on new mail in your own Inbox, first to last. The first rule that moves a message decides where it goes; marking read and flagging add up. Quarantine is never a target.',
  'mail.filters.none': 'No rules yet.',
  'mail.filters.new': 'New Rule',
  'mail.filters.back': 'Back to the Rules',
  'mail.filters.name': 'Name',
  'mail.filters.when': 'When a new message matches every condition',
  'mail.filters.then': 'Then',
  'mail.filters.and': 'and',
  'mail.filters.noValue': 'No value to type',
  'mail.filters.moveTo': 'Move to',
  'mail.filters.noMove': 'Do not move it',
  'mail.filters.markRead': 'Mark as read',
  'mail.filters.flag': 'Flag it',
  'mail.filters.skipInbox': 'Skip the Inbox',
  'mail.filters.suggestTag': 'Suggest Tag to Record',
  'mail.filters.stop': 'Stop running further rules',
  'mail.filters.enabled': 'On',
  'mail.filters.up': 'Up',
  'mail.filters.down': 'Down',
  'mail.filters.first': 'Already the first rule',
  'mail.filters.last': 'Already the last rule',
  'mail.filters.edit': 'Edit',
  'mail.filters.remove': 'Remove',
  'mail.filters.removeTitle': 'Remove this rule?',
  'mail.filters.removeBody': 'Mail it already moved stays where it is; new mail is no longer sorted by it.',
  'mail.filters.saved': 'Rule saved.',
  'mail.filters.run': 'Run Now Over the Inbox',
  'mail.filters.ranChanged': 'messages changed',
  'mail.filters.ranConsidered': 'looked at',
  'mail.filters.field.from': 'From',
  'mail.filters.field.to': 'To',
  'mail.filters.field.subject': 'Subject',
  'mail.filters.field.body': 'Message',
  'mail.filters.field.hasAttachment': 'Has an attachment',
  'mail.filters.comparator.contains': 'contains',
  'mail.filters.comparator.equals': 'is',
  'mail.filters.comparator.startsWith': 'starts with',
  'mail.filters.comparator.endsWith': 'ends with',
  'mail.filters.comparator.isTrue': 'is so',
  // Part c: a mailbox's record page and its Grants panel (decisions 0794, 0840).
  'mail.record.title': 'Mailbox',
  'mail.record.subtitle': 'Mailbox and who may use it',
  'mail.record.open': 'Who may use this mailbox',
  'mail.grants.title': 'Grants',
  'mail.grants.add': '+ Grant',
  'mail.grants.addTitle': 'Grant Access to This Mailbox',
  'mail.grants.give': 'Grant',
  'mail.grants.person': 'Person',
  'mail.grants.choosePerson': 'Choose a person',
  'mail.grants.capability': 'What they may do',
  'mail.grants.capability.read': 'Read',
  'mail.grants.capability.sendas': 'Send as',
  'mail.grants.capability.edit': 'Edit',
  'mail.grants.reason': 'Reason',
  'mail.grants.lastDay': 'Last day (optional)',
  'mail.grants.lastDayHint': 'The grant reads through its last day where the organization is, and stops the day after. Leave it empty to keep it until somebody presses End.',
  'mail.grants.lastDayIs': 'last day',
  'mail.grants.endedOn': 'ended, last day',
  'mail.grants.noEnd': 'no end date',
  'mail.grants.ended': 'Ended',
  'mail.grants.givenBy': 'Given by',
  'mail.grants.end': 'End',
  'mail.grants.endTitle': 'End this grant?',
  'mail.grants.endBody': 'Today becomes its last day: it still works until midnight where the organization is, and stops tomorrow. The grant stays listed as ended.',
  'mail.grants.none': 'Nobody holds a grant on this mailbox.',
  'mail.grants.departedHint': 'This mailbox belongs to somebody who has left. It offers Read and nothing else, so the manager can read what still arrives.',
  // Part c: a distribution list's members on its record page (decision 0841).
  'mail.members.title': 'Members',
  'mail.members.hint': 'Everybody in the department and the departments beneath it, as the org chart stands today, and each person given Read below. A message to the list goes to each of them once.',
  'mail.members.department': 'from the department',
  'mail.members.grant': 'added by a grant',
  'mail.members.noAddress': 'no address; skipped',
  'mail.members.none': 'Nobody is on this list yet, so a message to it would go nowhere and stay in Outbox.',
  // T369 (decision 1206): a mailbox watched only, and a super admin's switch to full.
  'mail.hostMode.watching': 'Watching Only',
  'mail.hostMode.full': 'Full Access',
  'mail.hostMode.watchingSince': 'Watching only since',
  'mail.hostMode.watchingHint': 'Nothing done here is written to the mailbox host: read marks, flags, moves, deletes and quarantine moves stay in this application, and the mail in Outlook or on the host is left as it is.',
  'mail.hostMode.fullHint': 'Read marks, flags, moves and deletes made here are written to the mailbox host, so Outlook and every other mail program see them.',
  'mail.hostMode.toFull': 'Switch to Full Access',
  'mail.hostMode.toFullTitle': 'Switch this mailbox to full access?',
  'mail.hostMode.toFullBody': 'From now on this application writes to the mailbox host: marking a message read or unread and flagging it, moving it between folders, deleting it to Trash, and the quarantine and filter-rule moves made when mail arrives. Every other mail program reading this mailbox sees those changes. What was done here while it was watched only is not replayed.',
  'mail.hostMode.toWatching': 'Switch to Watching Only',
  // T368 (decision 1205): Deliver a test message, the developer page that drops mail into the scripted host.
  'mail.deliver.title': 'Deliver a test message',
  'mail.deliver.intro': 'The message is dropped into the scripted mailbox host, so the next check for mail brings it in the way real mail arrives: screened, quarantined where screening says so, and with its attachments stored and scanned. Nothing leaves this environment.',
  'mail.deliver.unreachable': 'The page could not read its choices, so nothing can be delivered until the server answers.',
  'mail.deliver.refused': 'This deployment cannot deliver a test message.',
  'mail.deliver.noMailboxes': 'You read no mailbox yet, so there is nowhere to deliver a message.',
  'mail.deliver.mailbox': 'Deliver to',
  'mail.deliver.fromAddress': 'Sender address',
  'mail.deliver.fromName': 'Sender name',
  'mail.deliver.replyTo': 'Reply-To address',
  'mail.deliver.subject': 'Subject',
  'mail.deliver.body': 'Body',
  'mail.deliver.bills': 'Fixture bills to attach',
  'mail.deliver.uploads': 'Files to attach',
  'mail.deliver.verified': 'The mail host verified the sender',
  'mail.deliver.verifiedHint': 'Unticked, the message carries failed sender checks. With a Reply-To at another domain and wording about bank details, screening quarantines it.',
  'mail.deliver.deliverAndSync': 'Deliver and Sync Now',
  'mail.deliver.deliverOnly': 'Deliver Only',
  'mail.deliver.busy': 'A delivery is running; wait for it to finish.',
  'mail.deliver.delivered': 'The message is in the mailbox host and arrives on the next check for mail.',
  'mail.deliver.arrivedIn': 'The message arrived and stands in',
  'mail.deliver.notArrived': 'The message is in the mailbox host, but the check for mail did not bring it in; check for mail again from the mail client.',
  'mail.deliver.open': 'Open the message in the mail client',
  'dev.pieceThirtyTwo': 'Email client',
  'dev.page.deliverMail.lookFor': 'A mailbox you read, a sender, a subject, a body, fixture bills and uploads; Deliver and Sync Now links the message that arrived, in the Inbox or in Quarantine.',
  // Part c: a department mailbox's row and the heads who look after its readers (decision 0842).
  'mail.record.edit': 'Edit',
  'mail.record.editTitle': 'Edit This Mailbox',
  'mail.record.name': 'Name',
  'mail.record.address': 'Address',
  'mail.record.addressHint': 'The address must already exist at the mail host; changing it here does not create it there. The Accounting document inbox’s address is also the address accounting documents are expected at, and changes with it.',
  'mail.record.department': 'Department',
  'mail.record.quota': 'Quota (MB)',
  'mail.record.quotaHint': 'Empty means no quota. The folder tree and Settings warn at 80 percent; nothing is refused at 100.',
  'mail.manages.title': 'Mailboxes you look after',
  // T3803 (decision 12640): + New Mailbox on Settings › Email › Mailboxes, a super admin's window.
  'mail.newMailbox.button': '+ New Mailbox',
  'mail.newMailbox.title': 'New Mailbox',
  'mail.newMailbox.save': 'Save Mailbox',
  'mail.newMailbox.noHead': '{department} has no head, so no one was given access to {address}. Give access from the mailbox’s page.',
  'mail.newMailbox.headGone': 'The head of {department} no longer works here, so no one was given access to {address}. Give access from the mailbox’s page.',
  'mail.newMailbox.intro': 'Registers a department’s or a module’s mailbox that already exists at your mail host. Save asks the host for the address first and saves nothing if it refuses.',
  'mail.newMailbox.kind': 'Kind',
  'mail.newMailbox.name': 'Name',
  'mail.newMailbox.localPart': 'Address Before the @',
  'mail.newMailbox.domain': 'Domain',
  'mail.newMailbox.notVerified': 'not verified',
  'mail.newMailbox.noDomains': 'No domain is registered yet. Add one on Email domains and press Check Records there first.',
  'mail.newMailbox.department': 'Department',
  'mail.newMailbox.departmentHint': 'The department’s head reads the new mailbox from the start and adds its other readers.',
  'mail.newMailbox.module': 'Module',
  'mail.newMailbox.hostMode': 'Host Mode',
  'mail.newMailbox.quota': 'Quota (MB)',
  'mail.newMailbox.documentsInbox': 'Make This the Documents Inbox',
  'mail.newMailbox.documentsInboxHint': 'Documents mailed to this address are read as bills and receipts. The Documents Inbox now reads',
  'mail.newMailbox.documentsInboxNone': 'No Documents Inbox address is set yet.',
  'mail.newMailbox.why': 'Fill in the name and the address first.',
  'mail.settings.documentsInbox': 'Documents Inbox',
  // Part c: the person's own out-of-office, and a departed mailbox's reply (decision 0843).
  'mail.ooo.title': 'Out of Office',
  'mail.ooo.on': 'Send automatic replies while I am away',
  'mail.ooo.first': 'First day away',
  'mail.ooo.last': 'Last day away',
  'mail.ooo.message': 'Message senders receive',
  'mail.ooo.hint': 'Each sender is answered once while you are away, never a mailing list, another automatic reply or a no-reply address. Where the message says {{returnDate}}, the first working day after your last day is filled in. The company signature block and the legal footer follow it, never your own signature.',
  'mail.ooo.fromLeave': 'Set my out-of-office from my approved leave of two working days or more',
  'mail.ooo.fromLeaveHint': 'Your choice is kept now; leave requests are not in the application yet, so nothing sets it for you until they are.',
  'mail.ooo.needFirst': 'Choose the first day away.',
  'mail.ooo.needLast': 'Choose the last day away.',
  'mail.ooo.lastBeforeFirst': 'The last day away cannot be before the first.',
  'mail.ooo.saved': 'Out of office saved.',
  'mail.departed.title': 'Automatic reply',
  'mail.departed.hint': 'This person has left. Every new sender is answered once, pointing them to somebody who can help, until the reply is turned off or the mailbox is purged.',
  'mail.departed.successor': 'Point senders to',
  'mail.departed.keepSuccessor': 'Keep the person the message names',
  'mail.departed.message': 'Message senders receive',
  'mail.departed.on': 'Send this reply',
  'mail.departed.saved': 'Automatic reply saved.',
  'mail.departed.none': 'No automatic reply is set.',

  // T419, decision 1235: the Mail Settings window — its rail, its search and the phone's menu.
  'mail.settingsWindow.title': 'Mail Settings',
  'mail.settingsWindow.open': 'Mail Settings',
  'mail.settingsWindow.kindLine': 'Mail client settings',
  'mail.settingsWindow.back': 'Back to Mail',
  'mail.settingsWindow.search': 'Search settings',
  'mail.settingsWindow.searchHint': 'Signature, undo, spam…',
  'mail.settingsWindow.noMatch': 'No section matches that search.',
  'mail.settingsWindow.menu': 'Section',
  'mail.settingsWindow.rail': 'Mail settings sections',
  'mail.settingsWindow.notYours': 'This section is not one you can open. Choose another section from the list.',
  'mail.settingsWindow.saved': 'Your change is saved.',
  'mail.settingsWindow.group.personal': 'Personal',
  'mail.settingsWindow.group.administration': 'Administration',
  'mail.settingsWindow.section.viewOptions': 'View Options',
  'mail.settingsWindow.section.compose': 'Compose',
  'mail.settingsWindow.section.conversation': 'Conversation',
  'mail.settingsWindow.section.folders': 'Folders',
  'mail.settingsWindow.section.outOfOffice': 'Out of Office',
  'mail.settingsWindow.section.signature': 'Signature',
  'mail.settingsWindow.section.sendAs': 'Send As and Delegation',
  'mail.settingsWindow.section.filters': 'Filters',
  'mail.settingsWindow.section.mailboxes': 'Mailboxes',
  'mail.settingsWindow.section.groups': 'Groups',
  'mail.settingsWindow.section.sharedMailboxes': 'Shared Mailboxes',
  'mail.settingsWindow.section.archiveRetention': 'Archive and Retention',
  'mail.settingsWindow.section.recipientCertificates': 'Recipient Certificates',
  'mail.settingsWindow.section.spamQuarantine': 'Spam and Quarantine',
  'mail.settingsWindow.conversation.hint': 'On, the message list draws one row for each conversation, with the newest message, the people who wrote and a count. Off, it draws one row for each message. The choice is yours alone.',
  'mail.settingsWindow.ooo.hint': 'Your automatic reply while you are away. It answers each sender once and never answers a mailing list, a bounce or another automatic reply.',
  'mail.settingsWindow.filters.incoming': 'Incoming Filters',
  'mail.settingsWindow.filters.hint': 'Your rules run in this order on each message as it first arrives in your own mailbox. This client has no outgoing filters.',

  // T419, decision 1236: the window's personal pages — View Options, Compose, Folders, Signature, Send As and Delegation.
  'mail.settingsWindow.view.hint': 'These choices change how your own mailbox looks and nobody else’s.',
  'mail.settingsWindow.view.readingPane': 'Reading pane',
  'mail.settingsWindow.view.readingPane.right': 'Beside the message list',
  'mail.settingsWindow.view.readingPane.bottom': 'Below the message list',
  'mail.settingsWindow.view.readingFormat': 'Reading format',
  'mail.settingsWindow.view.readingFormat.rich': 'As the sender formatted it',
  'mail.settingsWindow.view.readingFormat.plainText': 'Plain text, without pictures or styles',
  'mail.settingsWindow.view.askReadReceipt': 'Ask for a read receipt on every message I write',
  'mail.settingsWindow.view.askReadReceiptHint': 'You can still untick it on any one message. A receipt comes back only if the reader agrees to send one.',
  'mail.settingsWindow.view.afterDelete': 'After deleting or moving the open message',
  'mail.settingsWindow.view.afterDelete.list': 'Show the message list',
  'mail.settingsWindow.view.afterDelete.next': 'Open the next message down',
  'mail.settingsWindow.view.afterDelete.previous': 'Open the message above',
  'mail.settingsWindow.compose.hint': 'These choices set how a new message, a reply and a forward start. You can still change each message before you send it.',
  'mail.settingsWindow.compose.replyFrom': 'Reply from',
  'mail.settingsWindow.compose.replyFrom.receivingMailbox': 'The mailbox the message arrived in, where you can send from it',
  'mail.settingsWindow.compose.replyFrom.ownMailbox': 'Always my own mailbox',
  'mail.settingsWindow.compose.replyHeader': 'Line above the quoted message',
  'mail.settingsWindow.compose.replyHeader.short': 'One line: On the date, the sender wrote',
  'mail.settingsWindow.compose.replyHeader.full': 'The message’s From, Sent, To and Subject',
  'mail.settingsWindow.folders.hint': 'Every mailbox you read, with its folders and how many messages each holds. System folders cannot be renamed or deleted, and a filter rule can move mail into any other folder.',
  'mail.settingsWindow.folders.system': 'System',
  'mail.settingsWindow.folders.custom': 'Your Folder',
  'mail.settingsWindow.folders.unread': 'unread',
  'mail.settingsWindow.folders.messages': 'messages',
  'mail.settingsWindow.folders.none': 'You read no mailbox yet, so there are no folders to show.',
  'mail.settingsWindow.signature.placement': 'Signature in replies and forwards',
  'mail.settingsWindow.signature.placement.aboveQuote': 'Above the quoted message',
  'mail.settingsWindow.signature.placement.belowQuote': 'Below the quoted message',
  'mail.signature.new': 'New Signature',
  'mail.signature.name': 'Signature name',
  'mail.signature.body': 'Signature',
  'mail.signature.use': 'Used on',
  'mail.signature.use.new': 'New Messages',
  'mail.signature.use.reply': 'Replies and Forwards',
  'mail.signature.addresses': 'From addresses',
  'mail.signature.everyAddress': 'Every Address',
  'mail.signature.addressesHint': 'Tick the addresses this signature is for. Leave every box clear to use it on every address you send from.',
  'mail.signature.preview': 'Preview',
  'mail.signature.edit': 'Edit',
  'mail.signature.remove': 'Remove',
  'mail.signature.done': 'Done',
  'mail.signature.none': 'You keep no signature of your own, so your messages carry the company block alone.',
  'mail.signature.saved': 'Your signatures are saved.',
  'mail.signature.listHint': 'Each message you write starts with the signature for its From address, or the one for every address. A reply or a forward takes a reply signature where you keep one.',
  'mail.delegation.hint': 'The mailboxes you may read or send from besides your own, and who may use yours. A delegation on your mailbox is given and ended on your mailbox’s page.',
  'mail.delegation.held': 'Mailboxes you use',
  'mail.delegation.given': 'People who use your mailbox',
  'mail.delegation.noneHeld': 'You hold no grant on another mailbox.',
  'mail.delegation.noneGiven': 'Nobody holds a grant on your mailbox.',
  'mail.delegation.until': 'until',
  'mail.delegation.openOwn': 'Open your mailbox’s page',
  'mail.delegation.noMailbox': 'You have no mailbox of your own, so nobody can be given one of yours.',
  'mail.delegation.capability.read': 'Read',
  'mail.delegation.capability.sendAs': 'Send As',

  // T419, decision 1237: the window's Administration pages — Mailboxes, Groups, Shared Mailboxes, Archive and Retention, Spam and Quarantine.
  'mail.admin.mailboxes.hint': 'Every mailbox the client reads, with its readers and its quota, then which mailbox each module sends from, the company block, the legal footer and the desktop client credentials. A row opens its mailbox’s page, where its readers are granted and ended.',
  'mail.admin.clientAccess.title': 'Desktop client access',
  'mail.admin.clientAccess.hint': 'The credentials issued for a desktop mail program, newest first. This screen lists them; it neither issues nor revokes one.',
  'mail.admin.clientAccess.default': 'Desktop client access for people with no setting of their own',
  'mail.admin.clientAccess.on': 'On',
  'mail.admin.clientAccess.off': 'Off',
  'mail.admin.clientAccess.none': 'No credential has been issued for a desktop client.',
  'mail.admin.clientAccess.issued': 'issued',
  'mail.admin.clientAccess.lastUsed': 'last used',
  'mail.admin.clientAccess.revoked': 'Revoked',
  'mail.admin.clientAccess.active': 'Active',
  'mail.admin.groups.hint': 'The organization’s group addresses. A message to one goes to every member: the people in its department and every department beneath it, and anyone given a Read grant on it. Members change on the group’s page.',
  'mail.admin.groups.none': 'The organization has no group address yet.',
  'mail.admin.groups.members': 'members',
  'mail.admin.shared.hint': 'The department and module mailboxes, who owns each, how many people read it and what you hold on it. A row opens the mailbox’s page.',
  'mail.admin.shared.none': 'The organization has no shared mailbox yet.',
  'mail.admin.shared.readers': 'readers',
  'mail.admin.shared.noAccess': 'No Access',
  'mail.admin.shared.role.owner': 'Owner',
  'mail.admin.shared.role.head': 'Department Head',
  'mail.admin.shared.role.read': 'Read',
  'mail.admin.shared.role.sendAs': 'Send As',
  'mail.admin.retention.hint': 'How long the organization keeps mail, and how long a departed person’s mailbox stands before anyone may purge it. A purge keeps who wrote, to whom, when, the subject and the record tag, and drops the body and attachments.',
  'mail.admin.retention.days': 'Keep mail for this many days (leave blank to keep it for ever)',
  'mail.admin.retention.departed': 'A departed person’s mailbox stands at least this many days before a purge',
  'mail.admin.retention.unread': 'No purge runs on either number yet: the client keeps every message until a purge is built.',
  'mail.admin.certificates.hint': 'The S/MIME certificate for each address mail is encrypted to. An expired certificate reads Not Encrypted, and a discovered one that differs waits for your review.',
  'mail.admin.certificates.none': 'No certificate is on file yet.',
  'mail.admin.certificates.state.active': 'Active',
  'mail.admin.certificates.state.expiringSoon': 'Expiring Soon',
  'mail.admin.certificates.state.notEncrypted': 'Not Encrypted',
  'mail.admin.certificates.state.differs': 'Differs',
  'mail.admin.certificates.source.manualUpload': 'Uploaded by hand',
  'mail.admin.certificates.source.autoDiscovered': 'Found automatically',
  'mail.admin.certificates.expires': 'expires',
  'mail.admin.certificates.review': 'Review',
  'mail.admin.certificates.reviewHint': 'A signed message brought a certificate that differs from the one on file. Accept it only if the recipient confirms they changed it.',
  'mail.admin.certificates.onFile': 'On file',
  'mail.admin.certificates.discovered': 'Discovered',
  'mail.admin.certificates.accept': 'Accept',
  'mail.admin.certificates.reject': 'Reject',
  'mail.admin.certificates.accepted': 'The discovered certificate is now the one on file.',
  'mail.admin.certificates.rejected': 'The discovered certificate was rejected, and the one on file stands.',
  'mail.admin.certificates.remove': 'Remove',
  'mail.admin.certificates.removeTitle': 'Remove Certificate',
  'mail.admin.certificates.removeBody': 'Mail to this address is no longer encrypted until another certificate is uploaded:',
  'mail.admin.certificates.removed': 'Certificate removed for',
  'mail.admin.certificates.address': 'Recipient address',
  'mail.admin.certificates.choose': 'Choose Certificate',
  'mail.admin.certificates.upload': 'Upload',
  'mail.admin.certificates.uploadNeeds': 'Type the address and choose the certificate file first.',
  'mail.admin.certificates.uploaded': 'Certificate uploaded for',
  'mail.admin.certificates.unreadable': 'That file could not be read. Choose the certificate file again.',
  'mail.admin.spam.hint': 'Senders and domains the screener always lets through or always holds in Quarantine, for every mailbox, checked before any score. A retired entry stops matching and stays on record.',
  'mail.admin.spam.allowed': 'Allowed Senders',
  'mail.admin.spam.blocked': 'Blocked Senders',
  'mail.admin.spam.domains': 'Domains',
  'mail.admin.spam.none': 'None yet.',
  'mail.admin.spam.kind': 'List',
  'mail.admin.spam.allow': 'Allow',
  'mail.admin.spam.block': 'Block',
  'mail.admin.spam.matchType': 'Match',
  'mail.admin.spam.address': 'A sender’s address',
  'mail.admin.spam.domain': 'A whole domain',
  'mail.admin.spam.value': 'Address or domain',
  'mail.admin.spam.reason': 'Why (optional)',
  'mail.admin.spam.add': 'Add to the List',
  'mail.admin.spam.retire': 'Retire',
  'mail.admin.spam.matches': 'matches',
  'mail.admin.spam.review': 'Open the Quarantine review',
  'mail.admin.spam.added': 'The entry is added.',
  'mail.admin.spam.retired': 'The entry is retired.',

  // T346, decision 1515: the accounting mailbox's Looks like block, its actions and the dealt-with mark.
  'mail.accounting.title': 'Looks like',
  'mail.accounting.reading': 'Reading the attachment…',
  'mail.accounting.billAgainstOrder': 'Looks like a bill against purchase order',
  'mail.accounting.billFromVendor': 'Looks like a bill from',
  'mail.accounting.remittance': 'Looks like a remittance for invoice',
  'mail.accounting.statement': 'Looks like a statement. There is no document to make from it.',
  'mail.accounting.nothing': 'Nothing here it recognizes.',
  'mail.accounting.nothing.noVisionKey': 'The attachment is a picture, and no Google Cloud Vision key is held to read it. Enter one in Settings › Documents › Document Reading.',
  'mail.accounting.nothing.noDocument': 'The message carries no PDF or picture that has cleared its scan.',
  'mail.accounting.nothing.unread': 'Its attachment carries no text to read.',
  'mail.accounting.nothing.unrecognised': 'The document names no open order, no invoice of ours and no vendor here.',
  'mail.accounting.openOrders': 'open orders for this vendor, and the Bill form offers each.',
  'mail.accounting.number': 'Number',
  'mail.accounting.date': 'Date',
  'mail.accounting.total': 'Total',
  'mail.accounting.readFrom': 'Read from',
  'mail.accounting.company': 'Matched to',
  'mail.accounting.matchedBy.sender': 'by the sender’s address',
  'mail.accounting.matchedBy.signature': 'by the company line in the sender’s signature',
  'mail.accounting.matchedBy.letterhead': 'by the document’s letterhead alone; the sender matched no company here',
  'mail.accounting.companyUnknown': 'No company here; neither the sender nor the letterhead matched one',
  'mail.accounting.confirm': 'Confirm',
  'mail.accounting.addBill': 'Add New Bill',
  'mail.accounting.addPayment': 'Add New Payment',
  'mail.accounting.addVendorCredit': 'Add New Vendor Credit',
  'mail.accounting.addExpense': 'Add New Expense',
  'mail.accounting.filed': 'Dealt with: filed against',
  // T3498: the record was saved but the filing call failed, so the message is offered Tag to Record instead of a second press.
  'mail.accounting.notFiled': 'The record was saved, but this message was not filed against it. Use Tag to Record on the message.',
  // Decision 11741 (T3526): a second Add New Expense press on a receipt that read nothing reuses the first press's line.
  'mail.accounting.expenseReused': 'This receipt read no merchant or amount, so the expense line the first press made is reused and no second line is made.',
  // Decision 10042 (T3425): what a filed expense line reads by where it carries neither a merchant nor a description.
  'mail.accounting.expenseLabel': 'Expense',
  'mail.accounting.readOnly': 'Making a document from this message is the Accounting module’s write.',
  'mail.accounting.billLine': 'Vendor invoice',
  // T3432, decision 9800: the dedicated Documents Inbox address a message was delivered to, and the classification it stamps.
  'mail.accounting.billUnmatched': 'Looks like a bill, from a sender no vendor here matches.',
  'mail.accounting.stamp': 'Classified as',
  'mail.accounting.stampBy': 'by the dedicated address',
  'mail.accounting.stampPassedOver': 'It also reached a dedicated address that comes after this one in the Settings order:',
  'mail.mark.dealtWith': 'Dealt with',
  // Settings › Email › Mailboxes: the Set up company mail stepper (piece 32; decision 1771).
  'mail.setup.title': 'Set up company mail',
  'mail.setup.hint': 'Four steps take company mail from a domain to a message that arrived. A step ticks itself once what it asks for is saved.',
  'mail.setup.domain.title': 'The domain’s records are published',
  'mail.setup.domain.body': 'Register the domain on Email domains and publish its SPF, DKIM and DMARC records at the DNS host. Then press Check Records on the domain. The step is done once the newest check reads Pass on SPF and DMARC. DKIM is welcome and not required.',
  'mail.setup.domain.noDomain': 'Only the placeholder domain is registered.',
  'mail.setup.domain.noCheck': 'The domain’s records have not been checked yet.',
  'mail.setup.domain.failing': 'The newest check did not pass SPF and DMARC. Not passed:',
  'mail.setup.domain.done': 'Passed SPF, DKIM and DMARC',
  'mail.setup.domain.doneNoDkim': 'Passed SPF and DMARC; DKIM is not set up',
  'mail.setup.domain.open': 'Open Email domains',
  'mail.setup.host.title': 'The mailbox host is reached',
  'mail.setup.host.body': 'The host answers once a mailbox has synced its Inbox. Press Sync on a mailbox in the mail client, and check the host settings where nothing arrives.',
  'mail.setup.host.none': 'No mailbox has synced its Inbox yet.',
  'mail.setup.host.done': 'Mailboxes synced',
  'mail.setup.host.open': 'Open the mail client',
  'mail.setup.shared.title': 'The shared and module mailboxes are made',
  'mail.setup.shared.body': 'A Department mailbox, such as Accounting, and a Module mailbox are made on their record pages. This list shows each of them once they exist.',
  'mail.setup.shared.none': 'A Department mailbox and a Module mailbox are both needed.',
  'mail.setup.waitsHost': 'Reach the mailbox host on step 2 first.',
  'mail.setup.shared.done': 'Department and Module mailboxes',
  'mail.setup.sending.title': 'The outbound sending test passed',
  'mail.setup.sending.body': 'Press Send Test Message on Sending. The test passes when the host accepts the message for your own address.',
  'mail.setup.sending.none': 'No test message has been accepted yet.',
  'mail.setup.sending.done': 'Test message accepted',
  'mail.setup.sending.open': 'Open Sending',

  // 🔒 T354 (decision 2640): the single keys, the list of them ? opens, the sender's avatar and Unsubscribe.
  'mail.shortcuts.title': 'Keyboard Shortcuts',
  'mail.shortcuts.hint': 'The keys work while no text field, menu or window has focus, and act on the open message.',
  'mail.shortcuts.compose': 'New message',
  'mail.shortcuts.reply': 'Reply',
  'mail.shortcuts.replyAll': 'Reply all',
  'mail.shortcuts.forward': 'Forward',
  'mail.shortcuts.archive': 'Archive',
  'mail.shortcuts.delete': 'Delete',
  'mail.shortcuts.flag': 'Flag or unflag',
  'mail.shortcuts.markRead': 'Mark read',
  'mail.shortcuts.markUnread': 'Mark unread',
  'mail.shortcuts.next': 'Next message down the list',
  'mail.shortcuts.previous': 'Previous message up the list',
  'mail.shortcuts.help': 'Show this list',
  'mail.shortcuts.key.compose': 'c',
  'mail.shortcuts.key.reply': 'r',
  'mail.shortcuts.key.replyAll': 'a',
  'mail.shortcuts.key.forward': 'f',
  'mail.shortcuts.key.archive': 'e',
  'mail.shortcuts.key.delete': '# or Delete',
  'mail.shortcuts.key.flag': 's',
  'mail.shortcuts.key.markRead': 'Shift+I',
  'mail.shortcuts.key.markUnread': 'Shift+U',
  'mail.shortcuts.key.next': 'j',
  'mail.shortcuts.key.previous': 'k',
  'mail.shortcuts.key.help': '?',
  'mail.unsubscribe.action': 'Unsubscribe',
  'mail.unsubscribe.title': 'Unsubscribe From This List?',
  'mail.unsubscribe.webBody': 'This opens the sender’s own unsubscribe page in a new tab, at',
  'mail.unsubscribe.mailBody': 'This sends one unsubscribe request from this mailbox to',
  'mail.unsubscribe.confirm': 'Unsubscribe',
  'mail.unsubscribe.opened': 'The sender’s unsubscribe page opened in a new tab.',
  'mail.unsubscribe.sent': 'Unsubscribe request sent to',
  'mail.unsubscribe.held': 'Unsubscribe request waiting in Outbox for your undo delay, to',

  // 🔒 T413 (decision 2641): the attachment viewer.
  'mail.lightbox.previous': 'Previous attachment',
  'mail.lightbox.next': 'Next attachment',
  'mail.lightbox.of': 'of',
  'mail.lightbox.download': 'Download',
  'mail.lightbox.newTab': 'Open in a New Tab',
  'mail.lightbox.reading': 'Reading the file…',
  'mail.lightbox.cannotDraw': 'This file cannot be shown here. Download it or open it in a new tab.',
} as const;
